import cx from 'classnames';
import { compact } from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';

import Button, { ButtonColorUnion, ButtonSizeUnion } from 'components/ButtonDeprecated';
import Link from 'components/Link';
import Row from 'components/Row';
import createChainedFunction from 'utils/createChainedFunction';

import './styles/Tabs.scss';

/**
 * ## Tabs of various styles.
 * ### Use tabs to organize and separate content into different facets that are all part of the same overarching object.
 *
 * ```example
 * <Tabs onTabChange={() => console.log('onTabChange')}>
 *   <TabPanel title={<span>Tab #1 title</span>}>
 *     Tab #1
 *   </TabPanel>
 *   <TabPanel title={<span>Tab #2 title</span>}>
 *     Tab #2
 *   </TabPanel>
 *   <TabPanel title={<span>Tab #3 title</span>}>
 *     Tab #3
 *   </TabPanel>
 * </Tabs>
 * ```
 *
 * ```example
 * <Tabs
 *   buttonSize="xsmall"
 *   tabAlign="center">
 *   <TabPanel title={<span>Tab #1 title</span>}>
 *     Tab #1
 *   </TabPanel>
 *   <TabPanel title={<span>Tab #2 title</span>}>
 *     Tab #2
 *   </TabPanel>
 *   <TabPanel title={<span>Tab #3 title</span>}>
 *     Tab #3
 *   </TabPanel>
 * </Tabs>
 * ```
 *
 * ```example
 * <Tabs type="button">
 *   <TabPanel title={<span>Tab #1 title</span>}>
 *     Tab #1
 *   </TabPanel>
 *   <TabPanel title={<span>Tab #2 title</span>}>
 *     Tab #2
 *   </TabPanel>
 *   <TabPanel title={<span>Tab #3 title</span>}>
 *     Tab #3
 *   </TabPanel>
 * </Tabs>
 * ```
 *
 * ```example
 * <Tabs type="bar">
 *   <TabPanel title={<span>Tab #1 title</span>}>
 *     Tab #1
 *   </TabPanel>
 *   <TabPanel title={<span>Tab #2 title</span>}>
 *     Tab #2
 *   </TabPanel>
 *   <TabPanel title={<span>Tab #3 title</span>}>
 *     Tab #3
 *   </TabPanel>
 * </Tabs>
 * ```
 *
 * ```example
 * <div style={{ background: '#1C2363', padding: 10 }}>
 *   <Tabs theme="light">
 *     <TabPanel title={<span>Tab #1 title</span>}>
 *       Tab #1
 *     </TabPanel>
 *     <TabPanel title={<span>Tab #2 title</span>}>
 *       Tab #2
 *     </TabPanel>
 *     <TabPanel title={<span>Tab #3 title</span>}>
 *       Tab #3
 *     </TabPanel>
 *   </Tabs>
 * </div>
 * ```
 *
 *
 * # TODO
 * * Reduce `buttonSize` options
 * * Rename `TabsWrapper` to `Tabs` & cleanup inner `Tabs` instance
 */

export type Props = {
  /** Button */
  buttonColor?: ButtonColorUnion;
  /** Button */
  buttonSize?: ButtonSizeUnion;
  /** Button */
  buttonFull?: boolean;
  flush?: boolean;
  orientation?: 'horizontal' | 'vertical';
  tabActive?: number;
  tabAlign?: 'left' | 'right' | 'center';
  tabHeaderClass?: string;
  theme?: 'dark' | 'light';
  type?: 'border' | 'button' | 'bar';
  children: React.ReactNode;
  onTabChange?: (arg0: number) => void;
  className?: string;
  /** When true, it will render all tabs. Non-active tabs will be rendered, but hidden **/
  renderAllTabs?: boolean;
};

type State = {
  tabActive: number;
};
class Tabs extends React.Component<Props, State> {
  static defaultProps: {
    buttonColor: ButtonColorUnion;
    buttonSize: ButtonSizeUnion;
    flush: true;
    orientation: 'horizontal' | 'vertical';
    tabAlign: 'left' | 'right' | 'center';
    tabHeaderClass: string;
    theme: 'dark' | 'light';
    type: 'border' | 'button' | 'bar';
    renderAllTabs: boolean;
  } = {
    buttonColor: 'mascot',
    buttonSize: 'medium',
    flush: true,
    orientation: 'horizontal',
    tabAlign: 'left',
    tabHeaderClass: '',
    theme: 'dark',
    type: 'border',
    renderAllTabs: false,
  };
  state = {
    tabActive: this.props.tabActive || 0,
  };

  componentWillReceiveProps({ tabActive }: Props) {
    if (typeof tabActive === 'number') {
      this.setState({ tabActive });
    }
  }

  setActive = (index: number, e: Event) => {
    this.setState({ tabActive: index });
    e.preventDefault();

    if (this.props.onTabChange) {
      this.props.onTabChange(index);
    }
  };

  getChildren = () => {
    return compact(React.Children.toArray(this.props.children));
  };

  renderBar = (title: string) => {
    return <div className="Tabs-bar">{title}</div>;
  };

  renderMenuItems = () => {
    const {
      buttonColor,
      buttonFull,
      buttonSize,
      orientation,
      tabAlign,
      tabHeaderClass,
      type: tabType,
      theme,
    } = this.props;
    const { tabActive } = this.state;

    const menuItems = this.getChildren().map((_panel, index) => {
      if (!_panel) {
        return null;
      }

      const panel = _panel as React.ReactElement;

      const onClick = panel.props.disableActivationOnClick
        ? panel.props.onClick
        : createChainedFunction(panel.props.onClick, this.setActive.bind(this, index));

      const body = panel.props.indicator ? (
        <React.Fragment>
          <span className="Tabs-actionItem-indicator" />
          {panel.props.title}
        </React.Fragment>
      ) : (
        panel.props.title
      );

      if (tabType === 'button') {
        return (
          <Button
            color={buttonColor}
            full={buttonFull}
            key={index}
            level={tabActive === index ? 'primary' : 'secondary'}
            size={buttonSize}
            className="Tabs--buttons-button"
            eventElementName={panel.props.eventElementName}
            onClick={onClick}>
            {body}
          </Button>
        );
      } else if (tabType === 'bar') {
        return (
          <Button
            color={tabActive === index ? 'mascot' : 'graphite'}
            full
            key={index}
            level={tabActive === index ? 'primary' : 'secondary'}
            size={buttonSize}
            className="Tabs--bar-button"
            eventElementName={panel.props.eventElementName}
            onClick={onClick}>
            {body}
          </Button>
        );
      } else {
        const { selectedBorderColor: borderColor } = panel.props;

        return (
          <li
            key={index}
            className={cx('Tabs--border-item', {
              'Tabs--border-item--hidden': panel.props.hidden,
              'Tabs--border-item-vertical': orientation === 'vertical',
            })}>
            <Link
              href="#"
              to={panel.props.to}
              weight={tabActive === index ? 'semibold' : 'normal'}
              className={cx(
                'Tabs--border-item-link',
                tabActive === index && 'active',
                borderColor && `Tabs--border-item-link--${borderColor}`
              )}
              eventElementName={panel.props.eventElementName}
              onClick={onClick}
              data-test-selector={
                panel.props.dataTestSelector
                  ? `tab-panel-link-${panel.props.dataTestSelector}`
                  : undefined
              }
              data-test={
                panel.props.dataTest ? `tab-panel-link-${panel.props.dataTest}` : undefined
              }>
              {body}
            </Link>
          </li>
        );
      }
    });

    if (tabType === 'button') {
      return (
        <nav
          className={cx('Tabs Tabs--buttons', {
            'Tabs--buttons-vertical': orientation === 'vertical',
          })}>
          {menuItems}
        </nav>
      );
    } else if (tabType === 'bar') {
      return <nav className={cx('Tabs', 'Tabs--bar', tabHeaderClass)}>{menuItems}</nav>;
    } else {
      return (
        <nav
          className={cx(
            'Tabs',
            'Tabs--border',
            tabHeaderClass,
            tabAlign && `Tabs--${tabAlign}`,
            theme && `Tabs--${theme}`
          )}>
          <ul className="Tabs-list Tabs--border-list">{menuItems}</ul>
        </nav>
      );
    }
  };

  renderSelectedTab() {
    return this.getChildren()[this.state.tabActive];
  }

  renderAllTabs() {
    return this.getChildren().map((child, index) => {
      const isActive = this.state.tabActive === index;
      const displayNoneStyle = { display: 'none' };
      return (
        <div className="TabsWrap--tabContainer" style={isActive ? {} : displayNoneStyle}>
          {child}
        </div>
      );
    });
  }

  render() {
    const { className, flush } = this.props;

    const navItems = flush ? (
      this.renderMenuItems()
    ) : (
      <Row size="xxxlarge">{this.renderMenuItems()}</Row>
    );

    return (
      <span className={cx('TabsWrap', className)}>
        {navItems}
        {this.props.renderAllTabs ? this.renderAllTabs() : this.renderSelectedTab()}
      </span>
    );
  }
}

export default Tabs;
